import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import SearchResultCard from './SearchResultCard';

const SearchResultsList = ({ 
  results, 
  searchQuery, 
  sortBy, 
  onSortChange, 
  selectedResults, 
  onSelectionChange, 
  onBulkAction,
  onNavigate,
  loading 
}) => {
  const [selectAll, setSelectAll] = useState(false);

  const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'date-desc', label: 'Date Modified (Newest)' },
    { value: 'date-asc', label: 'Date Modified (Oldest)' },
    { value: 'title-asc', label: 'Title (A-Z)' },
    { value: 'title-desc', label: 'Title (Z-A)' },
    { value: 'size-desc', label: 'File Size (Largest)' },
    { value: 'size-asc', label: 'File Size (Smallest)' }
  ];

  const bulkActions = [
    { value: 'download', label: 'Download Selected', icon: 'Download' },
    { value: 'analyze', label: 'Analyze Selected', icon: 'Brain' },
    { value: 'export', label: 'Export Metadata', icon: 'FileText' },
    { value: 'add-to-collection', label: 'Add to Collection', icon: 'FolderPlus' }
  ];

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      onSelectionChange(results?.map(result => result?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectResult = (resultId) => {
    const newSelection = selectedResults?.includes(resultId)
      ? selectedResults?.filter(id => id !== resultId)
      : [...selectedResults, resultId];
    
    onSelectionChange(newSelection);
    setSelectAll(newSelection?.length === results?.length);
  };

  const handleBulkAction = (action) => {
    if (selectedResults?.length > 0) {
      onBulkAction(action, selectedResults);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Searching documents...</p>
        </div>
      </div>
    );
  }

  if (results?.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Search" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No results found</h3>
          <p className="text-muted-foreground mb-4">
            Try adjusting your search terms or filters to find what you're looking for.
          </p>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>• Check your spelling</p>
            <p>• Try different keywords</p>
            <p>• Use broader search terms</p>
            <p>• Clear some filters</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-background">
      {/* Results Header */}
      <div className="sticky top-0 bg-background border-b border-border z-10 px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Selection and Bulk Actions */}
          <div className="flex items-center gap-4">
            <Checkbox
              label={`Select all ${results?.length} results`}
              checked={selectAll}
              onChange={(e) => handleSelectAll(e?.target?.checked)}
            />
            
            {selectedResults?.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {selectedResults?.length} selected
                </span>
                <div className="flex items-center gap-1">
                  {bulkActions?.map((action) => (
                    <Button
                      key={action?.value}
                      variant="outline"
                      size="sm"
                      onClick={() => handleBulkAction(action?.value)}
                      iconName={action?.icon}
                      iconSize={16}
                    >
                      {action?.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sort Options */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Sort by:</span>
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={onSortChange}
              className="w-48"
            />
          </div>
        </div>
      </div>
      {/* Results List */}
      <div className="p-6 space-y-6">
        {results?.map((result) => (
          <SearchResultCard
            key={result?.id}
            result={result}
            searchQuery={searchQuery}
            isSelected={selectedResults?.includes(result?.id)}
            onSelect={handleSelectResult}
            onNavigate={onNavigate}
          />
        ))}
      </div>
      {/* Load More */}
      {results?.length >= 20 && (
        <div className="p-6 text-center border-t border-border">
          <Button
            variant="outline"
            onClick={() => onBulkAction('load-more')}
            iconName="ChevronDown"
            iconSize={16}
          >
            Load More Results
          </Button>
        </div>
      )}
    </div>
  );
};

export default SearchResultsList;